import { isMobileOnly } from 'react-device-detect';
import {
  TCustomerCommonData,
  TCommonResponse,
} from '../models/common/customer';
import { TProfilePhone, ITradeType } from '../models/b2b/account-profile';
import { IB2CTradeTypesResponse } from '../models/b2c/account-profile';
import { IAddress } from '../models/b2c/addresses';

export const isSsr = typeof window === 'undefined';

export const getHost = (): string => {
  if (isSsr) return '';

  if (process.env.NODE_ENV === 'production' && !process.env.DOMAIN_NAME) {
    return `https://${window.location.hostname}`;
  }

  return window.location.origin;
};

export const getChannelID = (): number => (isMobileOnly ? 2 : 1);

export const generateXB3TraceID = (): string => {
  if (isSsr) return '';

  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join(
    '',
  );
};

export const getMaskedPhoneNumber = (phone?: string): string => {
  if (!phone) return '';
  return `******${phone.slice(-4)}`;
};

export const getHyphenatedPhoneNumber = (phone: string): string => {
  const phoneNumber = phone.replace(/\D/g, '');
  const phoneLength = phoneNumber.length;

  if (phoneLength > 3 && phoneLength <= 6) {
    return `${phoneNumber.substring(0, 3)}-${phoneNumber.substring(3)}`;
  }

  if (phoneLength > 6) {
    return `${phoneNumber.substring(0, 3)}-${phoneNumber.substring(
      3,
      6,
    )}-${phoneNumber.substring(6, 10)}`;
  }

  return phoneNumber;
};

export const sanitizePhoneNumber = (phoneNumber: string) =>
  phoneNumber.replace(/[-]/g, '');

const getPrimaryPhone = (phones?: TProfilePhone[]) =>
  phones?.find((value) => value.primaryFlag === 'Y');

const getPhonePreferenceNumber = (phone: TProfilePhone) =>
  phone.preferenceType
    ? parseInt(phone.preferenceType?.replace(/phn/i, ''), 10)
    : 9999;

const getSortedAltPhones = (phones?: TProfilePhone[]) =>
  phones
    ?.filter((value) => value.primaryFlag !== 'Y')
    .sort(
      (phone1, phone2) =>
        getPhonePreferenceNumber(phone1) - getPhonePreferenceNumber(phone2),
    )
    .map((value, index) => ({ ...value, id: index }));

export const getConfigurationValue = (value: string) =>
  typeof window !== 'undefined'
    ? window.__EXPERIENCE_PROPS__?.expConfiguration[value]
    : '';

export const mapResponseToCustomerModel = (
  response: TCommonResponse,
): TCustomerCommonData => {
  const customerProfile = response.accountProfile?.customerProfile;
  const companyDetails = customerProfile
    ? {
        companyName: customerProfile.businessName,
        address: customerProfile.address?.[0].postalDetails.addressLine1 || '',
        addressLine2: customerProfile.address?.[0].postalDetails.addressLine2,
        companyPhone: getPrimaryPhone(customerProfile.phones),
        businessType: customerProfile.tradeType,
        city: customerProfile.address?.[0].postalDetails.city || '',
        state: customerProfile.address?.[0].postalDetails.state || '',
        zipCode: customerProfile.address?.[0].postalDetails.zipCode || '',
        altPhones: getSortedAltPhones(customerProfile.phones),
      }
    : undefined;
  return {
    firstName:
      response.accountProfile?.userProfile.name.firstName ||
      response.account?.profile.name?.firstName,
    lastName:
      response.accountProfile?.userProfile.name.lastName ||
      response.account?.profile.name?.lastName,
    zipCode: response.account?.profile.zipCode,
    phones:
      response.accountProfile?.userProfile.phones ||
      response.account?.profilePhones?.phone,
    taxExemptId:
      response.accountProfile?.customerProfile.taxExemptId ||
      response?.account?.profile?.taxExemptId,
    localStoreId:
      response.accountProfile?.userProfile.localStoreId ||
      response?.account?.profile?.localStoreId,
    companyDetails,
    accountOwner: response.accountProfile?.userProfile.accountOwner || 'N',
    emailId:
      response.accountProfile?.userProfile?.emailId ||
      response?.account?.profile.emailId,
    response,
  };
};

export const mapTradeTypes = (data: IB2CTradeTypesResponse) => ({
  tradeTypes: data?.tradeTypes.map<ITradeType>(
    ({ typeEnumeration, typeDescription, segment }) => ({
      type: typeEnumeration,
      businessCategory: segment,
      description: typeDescription,
    }),
  ),
});

export const getTmxId = (): string => {
  const nativeTMXID = window.__EXPERIENCE_CONTEXT__?.isConsumerApp
    ? getUrlParam('guid')
    : '';
  return nativeTMXID || window.THD_ACCOUNT_LIB?.TMX?.getTMXsessionId();
};

export const getNativeStoreID = (): string => {
  const storeParam = window.__EXPERIENCE_CONTEXT__?.isConsumerApp
    ? getUrlParam('storeid')
    : '';
  return storeParam || '';
};

export const getUrlParam = (key: string) => {
  const searchParams =
    typeof window !== 'undefined' ? window?.location?.search : '';
  const urlParams = new URLSearchParams(searchParams);
  return urlParams.get(key);
};

export const convertToMDYYYY = (date: string) =>
  new Date(date).toLocaleDateString('en-US');

export const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: 'smooth',
  });
};

export const smoothScroll = () => {
  document.documentElement.style.scrollBehavior = 'smooth';
};

export const getReCaptchaKey = () => {
  if (typeof window !== 'undefined') {
    if (window.location.hostname.endsWith('homedepot.com')) {
      return '6LcGXvclAAAAAM7ZE9BVUDtZ_x5jDvPB4Ml1fYwK';
    }
    if (window.location.hostname.includes('qa74')) {
      return '6LeNZeAlAAAAAGEsAyo1uZoTvfgSdLUdlfyWpIYp';
    }
    if (window.location.hostname.includes('qa71')) {
      return '6LcjiJMlAAAAAK7W4IzEyg1u9Ls7hML6cSgJOh1U';
    }
  }

  return '';
};

export const isRecaptchaEnabled = (isB2B: boolean) =>
  getConfigurationValue(
    isB2B
      ? 'B2B_PROFILE_INVISIBLE_RECAPTCHA_ENABLED'
      : 'B2C_PROFILE_INVISIBLE_RECAPTCHA_ENABLED',
  ) === '1';

export const isRecaptchaEnterprise = (): boolean => {
  return getConfigurationValue('RECAPTCHA_ENTERPRISE') === '1';
};

export const minimumRecaptchaLength = (): number => {
  return Number(getConfigurationValue('MINIMUM_RECAPTCHA_LENGTH'));
};

export const getCookieByName = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const prefix = `${name}=`;
  const cookieValue = document.cookie
    .split('; ')
    .find((row) => row.startsWith(prefix))
    ?.split('=')[1];
  return cookieValue ? decodeURI(cookieValue) : null;
};

export const updateCurrentDefaultAdrFields = (
  newAdr: IAddress,
  curAdr: IAddress,
) => {
  const {
    name: curName,
    primaryPhone: curPrimaryPhone,
    postalDetails: curPostalDetails,
  } = curAdr;
  const {
    name: newName,
    primaryPhone: newPrimaryPhone,
    postalDetails: newPostalDetails,
  } = newAdr;

  return {
    ...curAdr,
    name: {
      ...curName,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      firstName: curName?.firstName?.trim() || newName?.firstName!.trim(),
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      lastName: curName?.lastName?.trim() || newName?.lastName!.trim(),
    },
    primaryPhone: curPrimaryPhone?.number?.trim()
      ? { ...curPrimaryPhone }
      : {
          ...newPrimaryPhone,
          // BE looks for the presence of this property and it determines if it is an "UPDATE" or "INSERT" action
          // undefined means that it will be an "INSERT"
          id: undefined,
        },
    postalDetails: {
      ...curPostalDetails,
      addressLine1:
        curPostalDetails?.addressLine1?.trim() || newPostalDetails.addressLine1,
    },
    isDefault: false,
  };
};

export const checkIsInviteExpirationEnabled = () => {
  return getConfigurationValue('USER_INVITE_EXPIRATION_ENABLED') === 'true';
};

// Navigation utility functions - can be mocked in tests
export const navigateToUrl = (url: string) => {
  window.location.assign(url);
};

export const replaceUrl = (url: string) => {
  window.location.replace(url);
};
